import axios from 'axios';
import { UploadResponse, ApiResponse } from '../../../shared/types';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 300000, // 5 minutes for large file uploads
});

export interface UploadOptions {
  file: File;
  expiresIn?: number;
  password?: string;
  maxDownloads?: number;
  onProgress?: (progress: number) => void;
}

export async function uploadFile(options: UploadOptions): Promise<UploadResponse> {
  const { file, expiresIn, password, maxDownloads, onProgress } = options;
  
  const formData = new FormData();
  formData.append('file', file);
  if (expiresIn) formData.append('expiresIn', expiresIn.toString());
  if (password) formData.append('password', password);
  if (maxDownloads) formData.append('maxDownloads', maxDownloads.toString());

  const response = await api.post<UploadResponse>('/api/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    onUploadProgress: (progressEvent) => {
      if (progressEvent.total && onProgress) {
        const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        onProgress(progress);
      }
    },
  });

  return response.data;
}

export async function getFileInfo(id: string): Promise<ApiResponse> {
  const response = await api.get<ApiResponse>(`/api/file/${id}`);
  return response.data;
}

export function getDownloadUrl(id: string, password?: string): string {
  const url = new URL(`${API_BASE_URL}/download/${id}`);
  if (password) {
    url.searchParams.set('password', password);
  }
  return url.toString();
}

export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

export function formatTimeRemaining(expiresAt: string): string {
  const now = new Date();
  const expiry = new Date(expiresAt);
  const diff = expiry.getTime() - now.getTime();
  
  if (diff <= 0) return 'Expired';
  
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
} 